import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import '../custom-table.css';


const GenericTable = ({
    data,
    loading,
    columnsConfig,
    rowKey = 'id',
    bordered = true,
    size = 'small',
    scroll = { x: 'max-content' },
    rowSelection,
    serverState,
    onServerChange
}) => {
    const [filteredData, setFilteredData] = useState([]);
    const [sortedInfo, setSortedInfo] = useState({});

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    const buildColumns = () => {
        return columnsConfig.map(column => {
            const columnConfig = {
                ...column,
                sorter: column.sortable ? (serverState ? true : (a, b) => {
                    if (column.sortable === true) {
                        if (typeof a[column.dataIndex] === 'number') {
                            return a[column.dataIndex] - b[column.dataIndex];
                        }
                        return String(a[column.dataIndex]).localeCompare(String(b[column.dataIndex]));
                    }
                    return column.sortable(a, b);
                }) : undefined,
                sortOrder: serverState ? (serverState.sort === column.dataIndex ? (serverState.direction === 'asc' ? 'ascend' : 'descend') : null) : sortedInfo.columnKey === column.dataIndex && sortedInfo.order,
            };

            if (column.searchable) {
                columnConfig.filterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                    <div style={{ padding: 8 }}>
                        <Input
                            placeholder={`Поиск ${column.title}`}
                            value={selectedKeys && selectedKeys[0]}
                            onChange={e => {
                                const value = e.target.value;
                                setSelectedKeys(value ? [value] : []);
                                confirm({ closeDropdown: false });
                            }}
                            onPressEnter={() => confirm()}
                            style={{ marginBottom: 8, display: 'block' }}
                        />
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => confirm()}
                                icon={<SearchOutlined />}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Поиск
                            </Button>
                            <Button
                                onClick={() => {
                                    clearFilters();
                                    confirm();
                                }}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Сбросить
                            </Button>
                        </Space>
                    </div>
                );
                columnConfig.filterIcon = filtered => (
                    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
                );
                columnConfig.onFilter = (value, record) =>
                    record[column.dataIndex]
                        ? String(record[column.dataIndex]).toLowerCase().includes(value.toLowerCase())
                        : false;
            }

            if (column.dateFilter) {
                columnConfig.filterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                    <div style={{ padding: 8 }}>
                        <DatePicker.RangePicker
                            value={selectedKeys && selectedKeys[0] ? [
                                dayjs(selectedKeys[0][0]),
                                dayjs(selectedKeys[0][1])
                            ] : null}
                            onChange={(dates, dateStrings) => {
                                setSelectedKeys(dates ? [dateStrings] : []);
                            }}
                            style={{ width: '100%', marginBottom: 8 }}
                        />
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => confirm()}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Фильтр
                            </Button>
                            <Button
                                onClick={() => {
                                    clearFilters();
                                    confirm();
                                }}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Сбросить
                            </Button>
                        </Space>
                    </div>
                );
                columnConfig.onFilter = (value, record) => {
                    if (!value || !value[0] || !record[column.dataIndex]) return true;

                    try {
                        const recordDate = dayjs(record[column.dataIndex]);
                        if (!recordDate.isValid()) return false;

                        const [start, end] = value[0];
                        const startDate = start ? dayjs(start) : null;
                        const endDate = end ? dayjs(end) : null;

                        console.log('Filtering date:', {
                            recordId: record[rowKey],
                            recordDate: recordDate.format('YYYY-MM-DD'),
                            filterRange: [start, end],
                            isValid: recordDate.isValid()
                        });

                        const isAfterStart = startDate ? recordDate.isSameOrAfter(startDate, 'day') : true;
                        const isBeforeEnd = endDate ? recordDate.isSameOrBefore(endDate, 'day') : true;

                        return isAfterStart && isBeforeEnd;
                    } catch (error) {
                        console.error('Date filtering error:', error);
                        return false;
                    }
                };
            }

            if (serverState) {
                delete columnConfig.onFilter;
                if (column.searchable) {
                    const value = column.dataIndex === 'employee_name' ? serverState.employeeQuery : serverState.nameQuery;
                    columnConfig.filteredValue = value ? [value] : null;
                }
                if (column.dateFilter) columnConfig.filteredValue = serverState.startDate && serverState.endDate ? [[serverState.startDate, serverState.endDate]] : null;
            }
            return columnConfig;
        });
    };

    const handleTableChange = (pagination, filters, sorter) => {
        if (serverState) {
            const dates = filters.created?.[0] || [];
            onServerChange({page:0, nameQuery:filters.name?.[0] || '', employeeQuery:filters.employee_name?.[0] || '',
                startDate:dates[0] || '', endDate:dates[1] || '',
                sort:sorter.order ? sorter.field : 'id', direction:sorter.order === 'ascend' ? 'asc' : 'desc'});
            return;
        }
        let filtered = [...data];

        Object.entries(filters).forEach(([key, value]) => {
            if (value) {
                const column = columnsConfig.find(col => col.key === key || col.dataIndex === key);
                if (column && column.onFilter) {
                    filtered = filtered.filter(record => {
                        try {
                            return column.onFilter(value, record);
                        } catch (error) {
                            console.error(`Filter error for column ${key}:`, error);
                            return true;
                        }
                    });
                }
            }
        });

        setFilteredData(filtered);
        setSortedInfo(sorter);
    };

    return (
        <div className="custom-table-container">
            <Table
                className="custom-table striped-table"
                columns={buildColumns()}
                dataSource={serverState ? data : filteredData}
                loading={loading}
                onChange={handleTableChange}
                rowKey={rowKey}
                bordered={bordered}
                size={size}

                /* 👇 Две ключевые строки */
                sticky                      // включает фиксирование шапки
                scroll={{ ...scroll, y: 520 }}  // обязательно для sticky header

                rowSelection={rowSelection}
                pagination={false}
            />
        </div>
    );
};

export default GenericTable;
